import React, { useEffect, useState } from 'react';
import {
  deleteActionCardDocument,
  deleteSchoolActionCardDocument,
  downloadActionCardDocument,
  downloadSchoolActionCardDocument,
  fetchActionCardDocuments,
  fetchSchoolActionCardDocuments,
  uploadActionCardDocument,
  uploadSchoolActionCardDocument,
} from '../services/api';

const ACCEPTED_FILES = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.rtf,.png,.jpg,.jpeg';

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Resources are shared per school and standard. Without a student, staff use the school-wide endpoints.
export default function ActionCardDocuments({ studentId, standardCode, canManage = false, schoolWide = false }: any) {
  const useSchoolRoutes = schoolWide && !studentId;
  const [documents, setDocuments] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [busy, setBusy] = useState<any>(false);
  const [error, setError] = useState<any>('');

  useEffect(() => {
    let cancelled = false;
    setDocuments([]);
    setError('');
    if ((!studentId && !useSchoolRoutes) || !standardCode) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (useSchoolRoutes ? fetchSchoolActionCardDocuments(standardCode) : fetchActionCardDocuments(studentId, standardCode))
      .then((items) => {
        if (!cancelled) setDocuments(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load school activity resources.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId, standardCode, useSchoolRoutes]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const document = await (useSchoolRoutes
        ? uploadSchoolActionCardDocument(standardCode, file)
        : uploadActionCardDocument(studentId, standardCode, file));
      setDocuments((current) => [document, ...current]);
    } catch (err: any) {
      setError(err.message || 'Could not upload school activity resource.');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (document) => {
    setBusy(true);
    setError('');
    try {
      const blob = await (useSchoolRoutes
        ? downloadSchoolActionCardDocument(document.document_id)
        : downloadActionCardDocument(studentId, document.document_id));
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename;
      link.rel = 'noopener noreferrer';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      setError(err.message || 'Could not download school activity resource.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Remove ${document.filename} from this school's Action Card?`)) return;
    setBusy(true);
    setError('');
    try {
      await (useSchoolRoutes
        ? deleteSchoolActionCardDocument(document.document_id)
        : deleteActionCardDocument(studentId, document.document_id));
      setDocuments((current) => current.filter((item) => item.document_id !== document.document_id));
    } catch (err: any) {
      setError(err.message || 'Could not remove school activity resource.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="School activity resources" style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #c3d2e7' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, color: '#1e3a8a', fontSize: 12, fontWeight: 800 }}>School Activity Resources</h4>
        {canManage && (
          <label style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 9px', border: '1px solid #9bb7df', borderRadius: 7, background: '#fff', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: busy || loading ? 'wait' : 'pointer', opacity: busy || loading ? 0.6 : 1 }}>
            {busy || loading ? 'Please wait…' : 'Add document'}
            <input
              type="file"
              aria-label="Add school activity resource"
              accept={ACCEPTED_FILES}
              disabled={busy || loading}
              onChange={handleUpload}
              style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}
            />
          </label>
        )}
      </div>
      {loading ? (
        <div role="status" style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>Loading school resources…</div>
      ) : documents.length > 0 ? (
        <ul style={{ display: 'grid', gap: 6, listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
          {documents.map((document) => (
            <li key={document.document_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '7px 8px', border: '1px solid #d5e1f0', borderRadius: 7, background: '#fff' }}>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere', color: '#334155', fontSize: 11 }}>
                <div style={{ fontWeight: 700 }}>{document.filename}</div>
                <div style={{ marginTop: 2, color: '#64748b', fontSize: 10 }}>{formatFileSize(document.file_size_bytes)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                <button type="button" disabled={busy} onClick={() => handleDownload(document)}
                  style={{ border: 0, background: 'transparent', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
                  Download
                </button>
                {canManage && (
                  <button type="button" disabled={busy} onClick={() => handleDelete(document)}
                    style={{ border: 0, background: 'transparent', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>
          {canManage ? 'Add a school activity document to share it with every family and student in your school.' : 'No school activity resources have been added yet.'}
        </div>
      )}
      {error && <div role="alert" style={{ marginTop: 8, color: '#991b1b', fontSize: 11 }}>{error}</div>}
      {canManage && <div style={{ marginTop: 6, color: '#64748b', fontSize: 10 }}>PDF, Word, PowerPoint, Excel, text, and image files · 25 MB maximum</div>}
    </section>
  );
}